/**
 * Account type enumeration.
 */
export type AccountType = "ASSET" | "LIABILITY" | "EQUITY" | "REVENUE" | "EXPENSE";

/**
 * Account subtype enumeration.
 */
export type AccountSubtype =
  | "CURRENT_ASSET"
  | "FIXED_ASSET"
  | "OTHER_ASSET"
  | "CURRENT_LIABILITY"
  | "LONG_TERM_LIABILITY"
  | "OWNERS_EQUITY"
  | "RETAINED_EARNINGS"
  | "OPERATING_REVENUE"
  | "OTHER_REVENUE"
  | "COST_OF_GOODS_SOLD"
  | "OPERATING_EXPENSE"
  | "OTHER_EXPENSE"
  | "TAX_EXPENSE";

/**
 * Valid subtypes for each account type.
 */
export const SUBTYPES_BY_TYPE: Record<AccountType, AccountSubtype[]> = {
  ASSET: ["CURRENT_ASSET", "FIXED_ASSET", "OTHER_ASSET"],
  LIABILITY: ["CURRENT_LIABILITY", "LONG_TERM_LIABILITY"],
  EQUITY: ["OWNERS_EQUITY", "RETAINED_EARNINGS"],
  REVENUE: ["OPERATING_REVENUE", "OTHER_REVENUE"],
  EXPENSE: ["COST_OF_GOODS_SOLD", "OPERATING_EXPENSE", "OTHER_EXPENSE", "TAX_EXPENSE"],
};

/**
 * Spanish labels for account subtypes.
 */
export const SUBTYPE_LABELS: Record<AccountSubtype, string> = {
  CURRENT_ASSET: "Activo Circulante",
  FIXED_ASSET: "Activo Fijo",
  OTHER_ASSET: "Otros Activos",
  CURRENT_LIABILITY: "Pasivo Circulante",
  LONG_TERM_LIABILITY: "Pasivo Largo Plazo",
  OWNERS_EQUITY: "Capital",
  RETAINED_EARNINGS: "Resultados Acumulados",
  OPERATING_REVENUE: "Ingresos Operacionales",
  OTHER_REVENUE: "Otros Ingresos",
  COST_OF_GOODS_SOLD: "Costo de Ventas",
  OPERATING_EXPENSE: "Gastos Operacionales",
  OTHER_EXPENSE: "Otros Gastos",
  TAX_EXPENSE: "Impuesto a la Renta",
};

/**
 * Account type from API response.
 * Matches the select shape in accounts.controller.ts
 */
export interface Account {
  id: string;
  code: string;
  name: string;
  accountType: AccountType;
  accountSubtype: AccountSubtype | null;
  normalBalance: "DEBIT" | "CREDIT";
  parentAccountId: string | null;
  level: number;
  isActive: boolean;
  isHeader: boolean;
  isSystemAccount: boolean;
  description: string | null;
}

/**
 * Account with children for tree rendering.
 */
export interface AccountTreeNode extends Account {
  children: AccountTreeNode[];
}

/**
 * API response shape for GET /accounts
 */
export interface AccountsResponse {
  accounts: Account[];
}
